/** @jsx jsx */
/**  @jsxFrag jsx  */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo, useMemo } from 'react';

import useRouter from 'js/lib/useRouter';
import { formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Grid } from '@coursera/cds-core';
import type { GridProps, ProductCardProps } from '@coursera/cds-core';

import { EnterpriseSearchCard } from 'bundles/enterprise-learner-search/components/search/EnterpriseSearchCard';
import type { EnterpriseCardData } from 'bundles/enterprise-learner-search/components/search/EnterpriseSearchCard';
import { useEnterpriseProductMetadata } from 'bundles/enterprise-learner-search/components/search/useEnterpriseProductMetadata';
import type { PrivateSession } from 'bundles/enterprise-learner-search/components/search/useEnterpriseProductMetadata';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import {
  launchMiniModal,
  shouldRedirectToUnifiedDescriptionPage,
} from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import { ProductsCardsCdsPlaceholder } from 'bundles/search-common/components/search-cards/Placeholder';
import type { SearchProductHit } from 'bundles/search-common/providers/searchTypes';
import { getIsPathwayContent, isShortFormContent } from 'bundles/search-common/utils/SearchUtils';
import { getCleanIdFromObjectId } from 'bundles/search-common/utils/getCleanIdFromObjectId';

import _t from 'i18n!nls/enterprise-learner-search';

// Re-export for backwards compatibility
export type { EnterpriseCardData };

const styles = {
  container: css`
    list-style: none;
    padding-left: 0;
  `,
};

type InputProps = {
  hits?: SearchProductHit[];
  isLoading?: boolean;
  noResults?: boolean;
  resultsPerPage: number;
  eventingData?: {
    searchIndex: string;
    searchIndexPosition: number;
    page?: string;
    searchQuery?: string;
    filtersApplied?: Record<string, string[]>;
  };
  programId?: string;
  thirdPartyOrganizationId?: string;
  renderSearchHit?: (hit: SearchProductHit, cardData?: EnterpriseCardData) => React.ReactNode;
  placeholder?: JSX.Element;
  gridSpacing?: GridProps['spacing'];
  variant?: ProductCardProps['variant'];
  renderAdminContent?: (hit: SearchProductHit) => ProductCardProps['adminContent'];
  onSearchCardClick?: (hit: SearchProductHit) => void;
};

type PropsToComponent = InputProps;

const getEnterprisePrivateSession = (id: string, privateSessions?: Array<PrivateSession | null>) => {
  return privateSessions?.find((session) => session?.productId === id);
};

export const combineHitData = (hits: SearchProductHit[], privateSessions?: Array<PrivateSession | null>) =>
  hits.map((hit) => {
    const searchableId = getCleanIdFromObjectId(hit.id || '') || '';
    const hitPrivateSession = getEnterprisePrivateSession(searchableId, privateSessions);

    if (hitPrivateSession?.startsAt && hitPrivateSession?.endsAt) {
      const startsAt = formatDateTimeDisplay(hitPrivateSession?.startsAt, 'MMM D');
      const endsAt = formatDateTimeDisplay(hitPrivateSession?.endsAt, 'MMM D, YYYY');
      const session = `${startsAt} - ${endsAt}`;

      return {
        ...hit,
        session,
      };
    } else {
      return hit;
    }
  });

export const convertHitToEnterpriseCardProps = (
  hit: SearchProductHit & { session?: string },
  hitPosition: number,
  cardData: EnterpriseCardData,
  urlToUse?: string,
  variant?: 'grid' | 'list',
  adminContent?: ProductCardProps['adminContent'],
  onSearchCardClick?: () => void
) => {
  return {
    id: hit.id || undefined,
    name: hit.translatedName || hit.name || undefined,
    slug: hit.url?.substring(hit.url.lastIndexOf('/') + 1),
    imageUrl: hit.imageUrl || undefined,
    url: hit.url || undefined,
    productType: hit.productType || undefined,
    productDifficultyLevel: hit.productDifficultyLevel || undefined,
    productDuration: undefined, // removed for feature parity, we do not show duration on enterprise
    duration: hit.duration || undefined,
    partners: hit.partners?.map((partner) => ({ name: partner })),
    partnerLogos: hit.partnerLogos || undefined,
    cobrandingEnabled: hit.cobrandingEnabled || undefined,
    skills: hit.skills || undefined,
    translatedSkills: hit.translatedSkills || undefined,
    avgProductRating: hit.avgProductRating || undefined,
    numProductRatings: hit.numProductRatings || undefined,
    isNewContent: hit.isNewContent || undefined,
    isPartOfCourseraPlus: hit.isPartOfCourseraPlus || undefined,
    isCreditEligible: hit.isCreditEligible || undefined,
    isCourseFree: hit.isCourseFree || undefined,
    isPathwayContent: getIsPathwayContent(hit),
    videosInLesson: hit.videosInLesson || undefined,
    parentCourseName: hit.translatedParentCourseName || hit.parentCourseName || undefined,
    parentLessonName: hit.translatedParentLessonName || hit.parentLessonName || undefined,
    completions: hit.completions || undefined,
    session: hit.session,
    adminContent,
    hitPosition,
    variant,
    urlToUse,
    cardData,
    onSearchCardClick,
  };
};

export const EnterpriseSearchCards = ({
  hits,
  noResults = false,
  resultsPerPage,
  eventingData,
  renderSearchHit,
  placeholder,
  gridSpacing,
  isLoading,
  variant,
  renderAdminContent,
  onSearchCardClick,
  programId,
  thirdPartyOrganizationId,
}: PropsToComponent) => {
  const router = useRouter();

  const products = useMemo(() => {
    const filteredHits = filterExistsOrDefault(hits);

    return filteredHits
      .filter((hit) => !!hit.id && !!hit.productType)
      .map(({ id }) => ({
        id: getCleanIdFromObjectId(id || '') || '',
      }));
  }, [hits]);

  const { privateSessions } = useEnterpriseProductMetadata(programId, products, thirdPartyOrganizationId);

  if (isLoading || !hits) {
    if (placeholder) return placeholder;
    return <ProductsCardsCdsPlaceholder {...{ resultsPerPage, variant }} />;
  }

  const combinedHits = combineHitData(hits, privateSessions);
  // Directly link to unified description page if no in multi-program home.
  const redirectToUnifiedDescriptionPage = shouldRedirectToUnifiedDescriptionPage(router);

  return (
    <Grid
      container
      component="ul"
      css={styles.container}
      spacing={gridSpacing}
      className="rc-EnterpriseSearchCards"
      role="region"
      aria-label={_t('Search Results')}
      id="searchResults"
    >
      {combinedHits.length < 1 && <div>{_t('No results found')}</div>}
      {combinedHits.map((hit: SearchProductHit & { session?: string }, hitPosition) => {
        const isSFC = isShortFormContent(hit.productType ?? undefined);
        const searchQuery = router.location.search;
        let urlToUse = redirectToUnifiedDescriptionPage
          ? `/programs/${router.params.programSlug}${hit.url}?source=search`
          : undefined;
        urlToUse = isSFC ? `${hit.url}${searchQuery}` : urlToUse;

        const cardData: EnterpriseCardData = {
          objectID: hit.id ?? '',
          hitPosition,
          query: eventingData?.searchQuery || '',
          queryID: eventingData?.searchQuery || '',
          listIndex: Number(eventingData?.page) || 1,
          hasNoResults: noResults,
          filtersApplied: eventingData?.filtersApplied || {},
          indexName: eventingData?.searchIndex,
          indexPosition: eventingData?.searchIndexPosition,
        };

        if (renderSearchHit) {
          return renderSearchHit(hit, cardData);
        }

        const adminContent = renderAdminContent?.(hit);
        let handleCardClick: (() => void) | undefined;
        if (onSearchCardClick) {
          handleCardClick = () => onSearchCardClick(hit);
        } else if (!urlToUse) {
          handleCardClick = () => {
            const productIdForMiniModal = getCleanIdFromObjectId(hit.id || '');
            if (!productIdForMiniModal) return;
            const isCourseType = (hit.productType || '').toLowerCase() === 'course';
            launchMiniModal(router, productIdForMiniModal, isCourseType, undefined);
          };
        }

        const cardProps = convertHitToEnterpriseCardProps(
          hit,
          hitPosition,
          cardData,
          urlToUse,
          variant,
          adminContent,
          handleCardClick
        );

        return <EnterpriseSearchCard key={hit.id} {...cardProps} />;
      })}
    </Grid>
  );
};

export default memo(EnterpriseSearchCards);
